import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import FormField, { Select } from '../../components/FormField';

const BINGO_COLS = ['B', 'I', 'N', 'G', 'O'];

export default function AdminBingo() {
  const [events, setEvents]     = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [active, setActive]     = useState(false);
  const [cards, setCards]       = useState([]);
  const [newName, setNewName]   = useState('');
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied]     = useState(null);

  // Game state
  const [drawn, setDrawn]       = useState([]);
  const [current, setCurrent]   = useState(null);
  const [remaining, setRemaining] = useState(75);
  const [drawing, setDrawing]   = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const applyGame = (g) => {
    setDrawn(g.drawn || []);
    setCurrent(g.current ?? null);
    setRemaining(g.remaining ?? 75);
  };

  const loadAll = useCallback(() => {
    if (!selectedEvent) return;
    api.get(`/api/bingo/admin/events/${selectedEvent}/settings`).then((r) => setActive(r.data.is_active));
    api.get(`/api/bingo/admin/events/${selectedEvent}/cards`).then((r) => setCards(r.data));
    api.get(`/api/bingo/admin/events/${selectedEvent}/game`).then((r) => applyGame(r.data));
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedEvent) { setCards([]); applyGame({ drawn: [], current: null, remaining: 75 }); setActive(false); return; }
    loadAll();
  }, [selectedEvent, loadAll]);

  const toggleActive = async () => {
    setToggling(true);
    try {
      const r = await api.put(`/api/bingo/admin/events/${selectedEvent}/settings`, { is_active: !active });
      setActive(r.data.is_active);
    } finally { setToggling(false); }
  };

  const createCard = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post(`/api/bingo/admin/events/${selectedEvent}/cards`, { name: newName.trim() });
      setCards([r.data, ...cards]);
      setNewName('');
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setCreating(false); }
  };

  const deleteCard = async (id) => {
    if (!window.confirm('Karte löschen?')) return;
    await api.delete(`/api/bingo/admin/cards/${id}`);
    setCards(cards.filter((c) => c.id !== id));
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/bingo/${token}`);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const drawNumber = async () => {
    setDrawing(true);
    try {
      const r = await api.post(`/api/bingo/admin/events/${selectedEvent}/game/draw`);
      applyGame(r.data);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setDrawing(false); }
  };

  const resetGame = async () => {
    if (!window.confirm('Wirklich alle gezogenen Zahlen löschen und eine neue Runde starten?')) return;
    setResetting(true);
    try {
      const r = await api.post(`/api/bingo/admin/events/${selectedEvent}/game/reset`);
      applyGame(r.data);
    } finally { setResetting(false); }
  };

  const manualCards = cards.filter((c) => !c.ticket_id);
  const ticketCards = cards.filter((c) => c.ticket_id);

  return (
    <div>
      <h2 className="page-title">Bingo</h2>

      <FormField label="Event auswählen" className="max-w-sm">
        <Select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {!selectedEvent ? (
        <div className="card text-center py-8 text-slate-400 text-sm mt-4">
          Bitte ein Event auswählen — Bingo-Karten und Ziehung werden pro Event separat verwaltet.
        </div>
      ) : (
        <>
          {/* Toggle */}
          <div className={`card mb-6 mt-4 flex items-center justify-between border-l-4 ${active ? 'border-l-emerald-400' : 'border-l-slate-300'}`}>
            <div>
              <p className="font-bold text-slate-800">Bingo-Modus</p>
              <p className="text-sm text-slate-500">
                {active ? 'Aktiv – Karten sind für Teilnehmer sichtbar.' : 'Deaktiviert – Karten sind versteckt.'}
              </p>
            </div>
            <Button onClick={toggleActive} loading={toggling} variant={active ? 'danger' : 'success'}>
              {active ? 'Deaktivieren' : 'Aktivieren'}
            </Button>
          </div>

          {/* ── Game board ── */}
          <div className="card mb-6">
            <p className="section-title mb-4">Ziehung</p>

            {/* Current number */}
            <div className="flex items-center gap-6 mb-5">
              <div className="flex-shrink-0 w-24 h-24 rounded-2xl flex flex-col items-center justify-center shadow-inner"
                style={{ background: current ? '#fbbf24' : '#f1f5f9', border: '2px solid #e2e8f0' }}>
                {current ? (
                  <>
                    <span className="text-xs font-bold text-amber-900 leading-none">
                      {BINGO_COLS[Math.ceil(current / 15) - 1]}
                    </span>
                    <span className="text-4xl font-black text-amber-900 leading-none">{current}</span>
                  </>
                ) : (
                  <span className="text-sm text-slate-400 font-medium text-center px-2">Noch keine Zahl</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-500 mb-1">
                  {remaining > 0 ? `${75 - remaining} von 75 Zahlen gezogen` : 'Alle 75 Zahlen wurden gezogen'}
                </p>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                  <div className="h-full bg-ocean-500 rounded-full transition-all"
                    style={{ width: `${((75 - remaining) / 75) * 100}%` }} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={drawNumber} loading={drawing} disabled={remaining === 0}>
                    Zahl ziehen
                  </Button>
                  <Button onClick={resetGame} loading={resetting} variant="danger">
                    Neu starten
                  </Button>
                </div>
              </div>
            </div>

            {/* Number grid – grouped by B/I/N/G/O columns */}
            <div className="grid grid-cols-5 gap-1">
              {BINGO_COLS.map((col) => (
                <div key={col} className="text-center text-xs font-black text-ocean-700 py-1 tracking-widest">{col}</div>
              ))}
              {Array.from({ length: 15 }, (_, row) =>
                BINGO_COLS.map((_, colIdx) => {
                  const num = colIdx * 15 + row + 1;
                  const isCurrent = num === current;
                  const isDrawn = drawn.includes(num);
                  return (
                    <div key={num}
                      className="rounded text-center py-1 text-sm font-bold transition-all select-none"
                      style={{
                        background: isCurrent ? '#fbbf24' : isDrawn ? '#1e4976' : '#f1f5f9',
                        color:      isCurrent ? '#78350f' : isDrawn ? '#e0f0ff' : '#94a3b8',
                      }}>
                      {num}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Manual card creation */}
          <div className="card mb-6">
            <p className="section-title mb-3">Karte manuell erstellen</p>
            <p className="text-sm text-slate-500 mb-3">
              Für Teilnehmer ohne Ticket — generiert einen Link, der direkt zur Karte führt.
            </p>
            <form onSubmit={createCard} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Name der Karte (z.B. Gast: Max)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
              />
              <Button type="submit" loading={creating}>Generieren</Button>
            </form>
          </div>

          {/* Manual cards */}
          {manualCards.length > 0 && (
            <div className="mb-6">
              <p className="section-title mb-3">Manuelle Karten ({manualCards.length})</p>
              <div className="space-y-2">
                {manualCards.map((c) => (
                  <div key={c.id} className="card flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{c.token.slice(0, 12)}…</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => copyLink(c.token)}>
                        {copied === c.token ? '✓ Kopiert' : '🔗 Link kopieren'}
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => window.open(`/bingo/${c.token}`, '_blank')}>
                        Vorschau
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteCard(c.id)}>
                        Löschen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ticket cards */}
          <div>
            <p className="section-title mb-3">Ticket-Karten ({ticketCards.length})</p>
            {ticketCards.length === 0 ? (
              <div className="card text-center py-8 text-slate-400 text-sm">
                Noch keine Karten aus Ticket-Bestätigungen generiert.
              </div>
            ) : (
              <div className="space-y-2">
                {ticketCards.map((c) => (
                  <div key={c.id} className="card flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">{c.buyer_name || `Ticket #${c.ticket_id}`}</p>
                      <p className="text-xs text-slate-400">
                        Ticket #{c.ticket_id} · {new Date(c.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => copyLink(c.token)}>
                        {copied === c.token ? '✓ Kopiert' : '🔗 Link'}
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => window.open(`/bingo/${c.token}`, '_blank')}>
                        Vorschau
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
